import { useState } from 'react';
import type { LucideIcon } from 'lucide-react';

export interface TabConfig {
  id: string;
  label: string;
  icon: LucideIcon;
  badge?: string;
  component: React.ReactNode;
}

interface TabHubProps {
  title: string;
  subtitle?: string;
  icon: LucideIcon;
  tabs: TabConfig[];
  defaultTab?: string;
  accentColor?: string;
}

export default function TabHub({ title, subtitle, icon: Icon, tabs, defaultTab, accentColor = '#3b82f6' }: TabHubProps) {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id || '');
  const activeConfig = tabs.find(t => t.id === activeTab) || tabs[0];

  return (
    <div style={{ padding: '24px', minHeight: '100vh', background: '#0a0e27' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <div style={{
          width: '48px', height: '48px', borderRadius: '12px',
          background: `${accentColor}22`, border: `1px solid ${accentColor}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <Icon size={24} style={{ color: accentColor }} />
        </div>
        <div>
          <h1 style={{ color: 'white', margin: 0, fontSize: '24px', fontWeight: 700 }}>{title}</h1>
          {subtitle && <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '14px' }}>{subtitle}</p>}
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{
        display: 'flex', gap: '4px', padding: '4px',
        background: '#0f172a', borderRadius: '10px', border: '1px solid #1e293b',
        marginBottom: '24px', flexWrap: 'wrap'
      }}>
        {tabs.map(tab => {
          const TabIcon = tab.icon;
          const isActive = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 16px', borderRadius: '8px', border: 'none',
                background: isActive ? accentColor : 'transparent',
                color: isActive ? 'white' : '#94a3b8',
                fontSize: '13px', fontWeight: isActive ? 600 : 500,
                cursor: 'pointer', transition: 'all 0.2s',
                whiteSpace: 'nowrap'
              }}
            >
              <TabIcon size={16} />
              {tab.label}
              {tab.badge && (
                <span style={{
                  fontSize: '10px', fontWeight: 700, padding: '2px 6px',
                  borderRadius: '4px', background: isActive ? 'rgba(255,255,255,0.2)' : '#334155',
                  color: isActive ? 'white' : '#94a3b8'
                }}>
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Active Tab Content */}
      <div>{activeConfig?.component}</div>
    </div>
  );
}
